import { Injectable, OnDestroy, effect, untracked } from '@angular/core';
import { Subscription } from 'rxjs';
import { SessionService } from '../account-service/session.service';
import { GrowlService } from '../ui-feedback/growl.service';
import { describeSavedActionGrowl, describeSystemGrowl } from './notification-growl-handlers';
import type { NotificationGrowl } from './notification-growl-handlers';
import { NotificationGrowlPreferencesService } from './notification-growl-preferences.service';
import { NotificationService } from './notification.service';

const eventCooldownMs = 45_000;
const maximumDedupeEntries = 128;

// Single app-wide orchestration point. Pages still own their saved-state refreshes and manual
// success growls; automatic messages never mutate records or initiate server writes.
@Injectable({ providedIn: 'root' })
export class NotificationGrowlService implements OnDestroy {
  // Private variables

  private readonly subscriptions = new Subscription();
  private readonly recentMessages = new Map<string, number>();
  private identity = '';
  private destroyed = false;

  // Constructors

  constructor(
    private readonly session: SessionService,
    private readonly notifications: NotificationService,
    private readonly preferences: NotificationGrowlPreferencesService,
    private readonly growls: GrowlService
  ) {
    effect(() => {
      const identity = this.sessionIdentity();
      untracked(() => this.switchIdentity(identity));
    });

    this.subscriptions.add(this.notifications.listenForSystemEvents().subscribe(event => {
      this.display(describeSystemGrowl(event));
    }));

    this.subscriptions.add(this.notifications.listenForUserActions().subscribe(notification => {
      this.display(describeSavedActionGrowl(notification));
    }));

    this.subscriptions.add(this.notifications.listenForConnectionRecovery().subscribe(() => {
      if (this.currentIdentity())
        this.preferences.refresh();
    }));
  }

  // Public methods

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.subscriptions.unsubscribe();
    this.recentMessages.clear();
    this.growls.clearNotifications();
  }

  // Private methods

  private display(candidate: NotificationGrowl | undefined): void {
    if (!candidate || !this.currentIdentity() ||
        !this.preferences.allows(candidate.notificationType, candidate.clubId))
      return;

    const now = Date.now();
    const lastShown = this.recentMessages.get(candidate.dedupeKey);

    if (lastShown !== undefined && now - lastShown < eventCooldownMs)
      return;

    this.recentMessages.set(candidate.dedupeKey, now);
    this.trimDedupeCache(now);
    this.growls.notification(candidate.message, candidate.action);
  }

  private trimDedupeCache(now: number): void {
    for (const [key, timestamp] of this.recentMessages)
    {
      if (now - timestamp >= eventCooldownMs)
        this.recentMessages.delete(key);
    }

    while (this.recentMessages.size > maximumDedupeEntries)
    {
      const oldest = this.recentMessages.keys().next().value;

      if (!oldest)
        break;

      this.recentMessages.delete(oldest);
    }
  }

  private switchIdentity(identity: string): void {
    if (identity === this.identity)
      return;

    this.identity = identity;
    this.recentMessages.clear();
    this.growls.clearNotifications();
  }

  private currentIdentity(): string {
    if (this.destroyed)
      return '';

    const identity = this.sessionIdentity();
    this.switchIdentity(identity);
    return identity;
  }

  private sessionIdentity(): string {
    const account = this.session.account();

    return this.session.status() === 'authenticated' && account?.id
      ? `${account.id}:${this.session.version}` : '';
  }
}
